import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../RootState';
import {settings} from '@/plugins/settings';
import {helpdeskAxios} from '@/plugins/helpdeskAxios';
import responseHandler from '@/plugins/errorHandling';

export interface IEmailTemplate {
  id: number;
  name: string;
  subject: string | null;
  body: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface EmailTemplatesState {
  templates: IEmailTemplate[];
  allTemplates: IEmailTemplate[];
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  errors: any;
}

const state: EmailTemplatesState = {
  templates:    [],
  allTemplates: [],
  currentPage:  1,
  lastPage:     1,
  total:        0,
  perPage:      15,
  errors:       {},
};

const getters: GetterTree<EmailTemplatesState, RootState> = {
  templates:    (state) => state.templates,
  allTemplates: (state) => state.allTemplates,
  currentPage:  (state) => state.currentPage,
  lastPage:     (state) => state.lastPage,
  total:        (state) => state.total,
  perPage:      (state) => state.perPage,
};

const mutations: MutationTree<EmailTemplatesState> = {
  errors(state, payload: any) { state.errors = payload; },

  templatesLoaded(state, payload: any) {
    if (Array.isArray(payload)) {
      state.allTemplates = payload;
    } else {
      state.templates    = payload.data;
      state.currentPage  = payload.current_page;
      state.lastPage     = payload.last_page;
      state.total        = payload.total;
      state.perPage      = payload.per_page;
    }
  },

  templateCreated(_state, _payload: any) {},

  templateUpdated(state, payload: IEmailTemplate) {
    const idx = state.templates.findIndex(t => t.id === payload.id);
    if (idx !== -1) state.templates.splice(idx, 1, payload);
  },

  templateDeleted(_state, _payload: any) {},
};

const BASE = () => settings.helpdeskEndpoint + 'v1/templates';

const actions: ActionTree<EmailTemplatesState, RootState> = {
  fetchTemplates: ({commit}, params?: {page?: number; per_page?: number; search?: string}) => {
    const query = new URLSearchParams();
    if (params && params.page)     query.set('page',     String(params.page));
    if (params && params.per_page) query.set('per_page', String(params.per_page));
    if (params && params.search)   query.set('search',   params.search);
    const qs = query.toString() ? '?' + query.toString() : '';
    return responseHandler.handle(
      () => helpdeskAxios({url: BASE() + qs, method: 'GET'}),
      commit, 'templatesLoaded',
    );
  },

  fetchAllTemplates: ({commit}) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + '/all', method: 'GET'}),
    commit, 'templatesLoaded',
  ),

  createTemplate: ({commit}, data: Partial<IEmailTemplate>) => responseHandler.handle(
    () => helpdeskAxios({url: BASE(), method: 'POST', data}),
    commit, 'templateCreated', null, false,
  ),

  updateTemplate: ({commit}, {id, data}: {id: number; data: Partial<IEmailTemplate>}) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + '/' + id, method: 'PATCH', data}),
    commit, 'templateUpdated',
  ),

  deleteTemplate: ({commit}, id: number) => responseHandler.handle(
    () => helpdeskAxios({url: BASE() + '/' + id, method: 'DELETE'}),
    commit, 'templateDeleted', null, false,
  ),
};

export const helpdeskEmailTemplatesModule: Module<EmailTemplatesState, RootState> = {
  namespaced: true, state, getters, mutations, actions,
};
